import fp from 'lodash/fp';
import Axios from 'axios';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import urlJoin from 'url-join';
import timeout from 'smart-timeout';
import baseConnection from './baseConnection';

const tenant = ('APP_ENV_CONFIG' in window) ? window.APP_ENV_CONFIG.AzureAd.TenantId : 'test';
const clientId = ('APP_ENV_CONFIG' in window) ? window.APP_ENV_CONFIG.AzureAd.ClientId : 'test';
const virtualDirectory = ('APP_ENV_CONFIG' in window) ? window.APP_ENV_CONFIG.APP_VIRTUAL_DIR || '/' : '/';
const authority = `https://login.microsoftonline.com/${tenant}/`;

const redirectUri = urlJoin(window.location.origin, virtualDirectory);

const msalConfig = {
    auth: {
        clientId,
        authority,
        redirectUri,
    },
    cache: {
        cacheLocation: 'sessionStorage',
    }
};

export const msalInstance = new PublicClientApplication(msalConfig);
window.msalInstance = msalInstance;
msalInstance.config.redirectUri = redirectUri;

let logoutFunction = null;
let logoutFunctionTime = null;


export const logoutOnInactivity = (idleSeconds, logoutPath) => {
    logoutFunction = () => {
        const currentLocation = {
            path: window.parent.location.pathname,
            search: window.parent.location.search,
            hash: window.parent.location.hash
        };
        window.parent.msalInstance.config.postLogoutRedirectUri = urlJoin(
            window.parent.msalInstance.config.redirectUri,
            logoutPath,
            `?originalLocation=${encodeURIComponent(btoa(JSON.stringify(currentLocation)))}`
        );
        //document.cookie = cookieToken('', 0);
        window.parent.msalInstance.logoutRedirect();
    };

    logoutFunctionTime = idleSeconds * 1000;
};

const userTokenRequest = {
    scopes: ['https://graph.microsoft.com/User.Read']
};

let cachedSignedToken = null;
let tokenExpiryTime = null;

const getToken = async () => {
    if (cachedSignedToken && tokenExpiryTime && new Date() < tokenExpiryTime) {
        return cachedSignedToken;
    }

    let accountObj;

    if (msalInstance.getAllAccounts() !== undefined) {
        const accounts = msalInstance.getAllAccounts();
        const [first] = accounts;
        accountObj = first;
    }

    try {
        const tokenResponse = await msalInstance.acquireTokenSilent({
            account: accountObj,
            userTokenRequest
        });

        const token = tokenResponse.idToken;

        if (logoutFunction) {
            if (timeout.exists('logout')) { timeout.clear('logout'); }
            timeout.set('logout', logoutFunction, logoutFunctionTime);
        }

       
        return token;     

    } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
            return msalInstance.acquireTokenRedirect({
                account: accountObj,
                userTokenRequest
            });
        } else {
            throw new Error(error.message);
        }
    }
};

// eslint-disable-next-line no-shadow
export const runWithMsal = async (msalInstance, app) => {
    // it must run in iframe too for refreshToken (parsing hash and get token)
    await msalInstance.handleRedirectPromise();

    // prevent iframe double app !!!
    if (window === window.parent) {

        try {
            await getToken();
            app();
        } catch (error) {
            msalInstance.acquireTokenRedirect(userTokenRequest);
        }

        // }
    }
};

let adfsAxios = null;
export default class adfsConnection extends baseConnection {
    constructor() {
        super();
        if (!adfsAxios) {
            adfsAxios = Axios.create({
                baseURL: process.env.REACT_APP_API_PATH,
                headers: {
                    'Cache-Control': 'no-cache',
                    Authorization: `Bearer ${getToken()}`
                }
            });
        }
        this.axisConnection = adfsAxios;
    }
}

export const authInterceptor = async (requestConfig) => fp.set(
    'headers.common.Authorization',
    `Bearer ${await getToken()}`
)(requestConfig);

export const errorInterceptors = [
    fp.identity,
    (response) => {
        const responseStatus = fp.get('response.status')(response);

        if (!responseStatus) return Promise.reject(new Error(response.response.data));

        let title;

        switch (responseStatus) {
            case 400:
                // Throw the full response as an error, allow the sagas to deal with the bad request;
                return Promise.reject(response);
            case 401:
                title = 'You must be authenticated to use this service.';
                break;
            case 403:
                title = 'You are not authorised to use this service.';
                break;
            case 404:
                title = 'The service you requested could not be found.';
                break;
            default:
                if (responseStatus < 500) {
                    title = 'There is an unknown error in the request.';
                } else {
                    title = 'There was a server error when attempting to process the request.';
                }
                break;
        }

        return Promise.reject(new Error(`${responseStatus} ${title} ${response.response.data}`));
    }
];
